import { HttpClient } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { environment } from '@environments/environment'
import { RegistrationFlowType } from '@models/enums'
import { REFRESH_TOKEN_KEY, SELECTED_COMPANY_KEY } from '@utils/storage.keys'
import { Observable, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'

interface LoginResponse {
  token?: string
  Token?: string
  accessToken?: string
  AccessToken?: string
  refreshToken?: string
  RefreshToken?: string
}

interface RefreshResponse {
  accessToken?: string
  AccessToken?: string
  refreshToken?: string
  RefreshToken?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient)

  isLoggedIn$ = signal(false)
  private _token = signal<string | null>(null)
  token$ = this._token.asReadonly()

  private safeStorageGet(key: string): string | null {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  }

  private safeStorageSet(key: string, value: string): void {
    try {
      localStorage.setItem(key, value)
    } catch {
      // ignore storage failures (Safari private mode, strict privacy settings)
    }
  }

  private safeStorageRemove(key: string): void {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore storage failures
    }
  }

  public login(username: string, password: string): Observable<void> {
    console.debug('[AuthService] apiEndPoint =', environment.apiEndPoint);
    return this.http
      .post<LoginResponse>(environment.apiEndPoint + '/Auth/login', {
        email: username,
        password: password,
      })
      .pipe(
        map((user) => {
          const accessToken =
            user?.token ?? user?.Token ?? user?.accessToken ?? user?.AccessToken
          const refreshToken = user?.refreshToken ?? user?.RefreshToken

          if (!accessToken || !refreshToken) {
            throw new Error('AUTH.LOGIN.INVALID_RESPONSE')
          }

          this._token.set(accessToken)
          this.isLoggedIn$.set(true)
          this.safeStorageSet(REFRESH_TOKEN_KEY, refreshToken)
        }),
      )
  }

  /**
   * register
   */
  public register(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    confirmPassword: string,
    captchaToken: string,
    language: string,
  ) {
    return this.http.post<{
      message: string
      registrationFlowType: RegistrationFlowType
    }>(environment.apiEndPoint + '/Auth/register', {
      email,
      password,
      confirmPassword,
      firstName,
      lastName,
      captchaToken,
      language,
    })
  }

  public getCaptchaSiteKey() {
    return this.http.get<{ siteKey: string }>(`${environment.apiEndPoint}/Configs/captcha-site-key`)
  }

  confirmAccount(email: string, code: string, language: string) {
    return this.http.post<{
      message: string
      registrationFlowType: RegistrationFlowType
    }>(environment.apiEndPoint + '/Auth/confirmAccount', {
      email,
      code,
      language,
    })
  }

  load() {
    const token = this.safeStorageGet(REFRESH_TOKEN_KEY)
    if (!token) {
      return of(false)
    }
    return this.http
      .post<RefreshResponse>(
        environment.apiEndPoint + '/Auth/refreshAccessToken',
        {
          refreshToken: token,
        },
        { withCredentials: false },
      )
      .pipe(
        map((response) => {
          const accessToken = response.accessToken ?? response.AccessToken
          const refreshToken = response.refreshToken ?? response.RefreshToken
          if (!accessToken || !refreshToken) {
            throw new Error('AUTH.LOGIN.INVALID_RESPONSE')
          }

          this.isLoggedIn$.set(true)
          this._token.set(accessToken)
          this.safeStorageSet(REFRESH_TOKEN_KEY, refreshToken)
          return true
        }),
        catchError(() => {
          this._token.set(null)
          this.safeStorageRemove(REFRESH_TOKEN_KEY)
          this.safeStorageRemove(SELECTED_COMPANY_KEY)
          return of(false)
        }),
      )
  }

  logout() {
    return this.http
      .post(environment.apiEndPoint + '/Auth/logout', {
        refreshToken: this.safeStorageGet(REFRESH_TOKEN_KEY),
      })
      .pipe(
        tap(() => {
          this.safeStorageRemove(REFRESH_TOKEN_KEY)
          this.safeStorageRemove(SELECTED_COMPANY_KEY)
          this.isLoggedIn$.set(false)
          this._token.set(null)
        }),
      )
  }

  resendVerifyEmailEMail(email: string, language: string) {
    return this.http.post(
      `${environment.apiEndPoint}/Auth/sendEmailVerification`,
      { email, language },
    )
  }

  sendResetPasswordMail(email: string, language: string) {
    return this.http.post<{
      message: string
      flowType: number
    }>(`${environment.apiEndPoint}/Auth/sendResetMail`, { email, language })
  }

  resetPassword(token: string, password: string, confirmPassword: string) {
    return this.http.post<{
      message: string
      flowType: number
    }>(`${environment.apiEndPoint}/Auth/resetPassword`, {
      token,
      password,
      confirmPassword,
    })
  }
}
